import { useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

/** Mirror of the backend UK-mobile check (orders.ts isUkMobile). */
export function isUkMobile(raw: string): boolean {
  const p = (raw || '').replace(/[\s()-]/g, '');
  return /^(\+447\d{9}|07\d{9})$/.test(p);
}

/**
 * Shown only when an InPost-locker order is attempted without a mobile on file.
 * A logged-in buyer's profile number is used automatically (the server falls
 * back to it), so this never appears for them; if there is none, we collect one
 * here, save it to the profile, and let the purchase retry.
 */
export function LockerMobilePrompt({ open, onClose, onSaved }: {
  open: boolean;
  onClose: () => void;
  onSaved: (phone: string) => void;
}) {
  const user = useAuthStore((s) => s.user);
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  if (!open) return null;

  async function save() {
    const trimmed = phone.trim();
    if (!isUkMobile(trimmed)) { toast.error('Enter a valid UK mobile, e.g. 07123 456789.'); return; }
    setSaving(true);
    try {
      if (user) await api.put('/me/profile', { phone: trimmed });
      onSaved(trimmed);
    } catch {
      toast.error('Could not save your mobile number. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-cream border border-warmline shadow-frame p-5">
        <div className="text-2xl" aria-hidden>📦</div>
        <h2 className="display text-xl mt-2">Add a mobile for locker pickup</h2>
        <p className="text-[13px] text-mute mt-1.5 leading-snug">
          InPost lockers open with a one-time code the courier texts you. Add a UK mobile and we&rsquo;ll use it for this and your future locker orders.
        </p>
        <input
          autoFocus
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
          placeholder="07123 456789"
          className="input mt-4"
          aria-label="UK mobile number"
        />
        <div className="flex gap-2 mt-4">
          <button type="button" onClick={onClose} className="btn btn-outline btn-sm flex-1">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="btn btn-gold btn-sm flex-[1.4]">
            {saving ? 'Saving…' : 'Save & continue'}
          </button>
        </div>
        <p className="text-[10.5px] text-mute mt-2.5">Prefer home delivery? Close this and choose Royal Mail or DPD instead.</p>
      </div>
    </div>
  );
}
